'use client';

import { ReactNode, useState, useMemo, useRef } from 'react';
import {
	Microscope,
	ListOrdered,
	Search,
	Wrench,
	BarChart3,
	XCircle,
	Rocket,
	ChevronDown,
	ChevronRight,
	CheckCircle2,
	Loader2,
	StopCircle,
	DatabaseBackup,
	Brain,
	Sparkles,
	MessageCircleQuestion,
	ShieldCheck
} from 'lucide-react';
import { AgentActivity } from '@/types';
import JsonViewer from '@/_components/JsonViewer/JsonViewer';
import { useStickToBottom } from '@/_hooks/useStickToBottom.client';

interface AgentActivityLogProps {
	activities: AgentActivity[];
	/** Model thinking/reasoning text streamed during response generation */
	thinkingText?: string;
	/** Whether a stream is currently active */
	isStreaming?: boolean;
}

const ICON_CLASS = 'h-4 w-4 text-muted-foreground flex-shrink-0';

const categoryIcons: Record<AgentActivity['category'], ReactNode> = {
	classification: (
		<Microscope
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	schema_search: (
		<Search
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	query_generated: (
		<Wrench
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	query_result: (
		<BarChart3
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	error: (
		<XCircle
			className='h-4 w-4 flex-shrink-0 text-red-400'
			strokeWidth={1.5}
		/>
	),
	data_source_routing: (
		<Rocket
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	pool_reuse: (
		<DatabaseBackup
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	),
	stopped: (
		<StopCircle
			className='h-4 w-4 flex-shrink-0 text-red-500'
			strokeWidth={1.5}
		/>
	),
	thinking: (
		<Brain
			className='h-4 w-4 flex-shrink-0 text-purple-400'
			strokeWidth={1.5}
		/>
	),
	validation: (
		<ShieldCheck
			className={ICON_CLASS}
			strokeWidth={1.5}
		/>
	)
};

// Group activities into logical sections
interface ActivityGroup {
	id: string;
	type: 'header' | 'step' | 'response';
	title: string;
	activities: AgentActivity[];
	stepNumber?: number;
	/** Ordering key — timestamp of first activity in the group (ms since epoch) */
	firstTimestamp?: number;
}

/** Derive the status of an activity group from its activities */
function getGroupStatus(activities: AgentActivity[]) {
	const hasError = activities.some((a) => a.category === 'error');
	const hasStopped = activities.some((a) => a.category === 'stopped');
	const hasResult = activities.some(
		(a) =>
			a.category === 'query_result' ||
			a.category === 'pool_reuse' ||
			a.title.includes('Result') ||
			a.title.includes('Extracted Value')
	);
	const hasOnlyQuery = activities.every(
		(a) =>
			a.category === 'query_generated' ||
			a.category === 'classification' ||
			a.category === 'schema_search' ||
			a.category === 'thinking'
	);
	return { hasError, hasStopped, hasResult, hasOnlyQuery };
}

/** Get ms timestamp from an activity's timestamp field */
function activityMs(activity: AgentActivity): number {
	return new Date(activity.timestamp).getTime() || 0;
}

/** Derive a display title for a phase group based on its activities */
function derivePhaseTitle(activities: AgentActivity[]): string {
	// Check for common phase patterns in order of specificity
	const hasAdequacy = activities.some((a) => a.title.includes('Data Adequacy'));
	const hasReplan = activities.some((a) => a.title.includes('Replanned'));
	const hasPlan = activities.some((a) => a.title.includes('Query Plan'));

	if (hasAdequacy && hasReplan) return 'Replanning';
	if (hasAdequacy) return 'Data Adequacy Check';
	if (hasReplan) return 'Replanning';
	if (hasPlan) return 'Query Planning';

	return 'Query Planning';
}

/**
 * Group activities chronologically, interleaving phase groups (non-step activities)
 * with step groups. This ensures "Data Adequacy: Fail" and "Replanned" activities
 * appear in their correct chronological position between step execution waves,
 * rather than all being lumped into a single "Query Planning" header.
 */
function groupActivities(activities: AgentActivity[]): ActivityGroup[] {
	const responseActivities: AgentActivity[] = [];
	const stepMap = new Map<string, { activities: AgentActivity[]; stepNumber: number; title: string }>();

	// Phase groups: non-step, non-response activities grouped into chronological batches.
	// A new phase starts when a step-activity gap is detected.
	const phaseGroups: { activities: AgentActivity[] }[] = [];
	let currentPhase: AgentActivity[] = [];
	let lastWasStep = false;

	activities.forEach((activity) => {
		// "Model Reasoning" thinking (from format_response) goes to response generation group
		if (activity.category === 'thinking' && activity.title === 'Model Reasoning') {
			responseActivities.push(activity);
			return;
		}

		// Determine if this activity belongs to a step
		const stepId = activity.stepId || extractLegacyStepId(activity);

		if (stepId) {
			// Flush current phase batch before step activities (if transitioning from phase → step)
			if (!lastWasStep && currentPhase.length > 0) {
				phaseGroups.push({ activities: [...currentPhase] });
				currentPhase = [];
			}
			lastWasStep = true;

			if (!stepMap.has(stepId)) {
				const stepNumMatch = stepId.match(/^step_(\d+)/);
				const num = stepNumMatch ? parseInt(stepNumMatch[1], 10) : 0;
				const titleMatch = activity.title.match(/^Step\s+\d+:\s*(.*)/i);
				stepMap.set(stepId, {
					activities: [],
					stepNumber: num,
					title: titleMatch ? titleMatch[1] : activity.title
				});
			}
			stepMap.get(stepId)!.activities.push(activity);
			const titleMatch = activity.title.match(/^Step\s+\d+:\s*(.*)/i);
			if (titleMatch) {
				stepMap.get(stepId)!.title = titleMatch[1];
			}
		} else {
			// Non-step activity — start a new phase batch if transitioning from step → phase
			if (lastWasStep && currentPhase.length > 0) {
				phaseGroups.push({ activities: [...currentPhase] });
				currentPhase = [];
			}
			lastWasStep = false;
			currentPhase.push(activity);
		}
	});

	// Flush remaining phase
	if (currentPhase.length > 0) {
		phaseGroups.push({ activities: [...currentPhase] });
	}

	// Build all groups with timestamps for chronological sorting
	const groups: ActivityGroup[] = [];

	// Add phase groups with derived titles
	phaseGroups.forEach((phase, idx) => {
		groups.push({
			id: `phase_${idx}`,
			type: 'header',
			title: derivePhaseTitle(phase.activities),
			activities: phase.activities,
			firstTimestamp: activityMs(phase.activities[0])
		});
	});

	// Add step groups
	for (const [stepId, data] of stepMap.entries()) {
		groups.push({
			id: stepId,
			type: 'step',
			title: data.title,
			activities: data.activities,
			stepNumber: data.stepNumber,
			firstTimestamp: activityMs(data.activities[0])
		});
	}

	// Sort all groups chronologically by first activity timestamp
	groups.sort((a, b) => (a.firstTimestamp ?? 0) - (b.firstTimestamp ?? 0));

	// Add response generation at the end
	if (responseActivities.length > 0) {
		groups.push({
			id: 'response-generation',
			type: 'response',
			title: 'Response Generation',
			activities: responseActivities,
			firstTimestamp: activityMs(responseActivities[0])
		});
	}

	return groups;
}

/** Extract a synthetic stepId from legacy activities that use "Step N:" title format */
function extractLegacyStepId(activity: AgentActivity): string | null {
	const match = activity.title.match(/^Step\s+(\d+):/i);
	return match ? `step_${match[1]}` : null;
}

function getStepStatusIcon(group: ActivityGroup, isStreaming: boolean) {
	if (group.type === 'response') {
		return (
			<Sparkles
				className='h-5 w-5 text-amber-400'
				strokeWidth={2}
			/>
		);
	}

	const { hasError, hasStopped, hasResult, hasOnlyQuery } = getGroupStatus(group.activities);

	if (hasError) {
		return (
			<XCircle
				className='h-5 w-5 text-red-400'
				strokeWidth={2}
			/>
		);
	}

	if (hasStopped) {
		return (
			<StopCircle
				className='h-5 w-5 text-red-500'
				strokeWidth={2}
			/>
		);
	}

	if (hasResult) {
		return (
			<CheckCircle2
				className='h-5 w-5 text-emerald-400'
				strokeWidth={2}
			/>
		);
	}

	if (isStreaming && hasOnlyQuery) {
		return (
			<Loader2
				className='h-5 w-5 animate-spin text-blue-400'
				strokeWidth={2}
			/>
		);
	}

	// Default to complete
	return (
		<CheckCircle2
			className='h-5 w-5 text-emerald-400'
			strokeWidth={2}
		/>
	);
}

function formatTimestamp(date: Date | string): string {
	const d = new Date(date);
	const hours = d.getHours().toString().padStart(2, '0');
	const minutes = d.getMinutes().toString().padStart(2, '0');
	const seconds = d.getSeconds().toString().padStart(2, '0');
	return `${hours}:${minutes}:${seconds}`;
}

interface GroupCardProps {
	group: ActivityGroup;
	isStreaming: boolean;
	defaultExpanded?: boolean;
}

function GroupCard({ group, isStreaming, defaultExpanded = false }: GroupCardProps) {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const statusIcon = getStepStatusIcon(group, isStreaming);

	// Get summary for collapsed view
	const summary = useMemo(() => {
		if (group.type === 'header') {
			// For phase groups, show the most informative activity as summary
			const queryPlan = group.activities.find((a) => a.title.includes('Query Plan:'));
			if (queryPlan) return queryPlan.title;
			const replan = group.activities.find((a) => a.title.includes('Replanned'));
			if (replan) return replan.title;
			const adequacy = group.activities.find((a) => a.title.includes('Data Adequacy'));
			if (adequacy) return adequacy.title;
			const lastActivity = group.activities[group.activities.length - 1];
			return lastActivity?.title || 'Planning query execution';
		}
		// Check for extracted value first
		const extracted = group.activities.find((a) => a.title.includes('Extracted Value'));
		if (extracted) {
			const match = extracted.content.match(/Value:\s*(.+?)(?:\n|$)/);
			return match ? `Extracted: ${match[1].slice(0, 50)}${match[1].length > 50 ? '...' : ''}` : 'Complete';
		}
		const { hasResult, hasOnlyQuery } = getGroupStatus(group.activities);
		if (hasResult) {
			const poolReused = group.activities.some((a) => a.category === 'pool_reuse');
			return poolReused ? 'Reused from pool' : 'Data fetched';
		}
		return hasOnlyQuery ? 'In progress...' : 'Complete';
	}, [group]);

	const lastTimestamp = group.activities[group.activities.length - 1]?.timestamp;

	return (
		<div className='border-border bg-secondary/50 animate-[fadeSlideIn_0.25s_ease-out] overflow-hidden rounded-lg border'>
			{/* Header - main group toggle */}
			<button
				type='button'
				aria-expanded={isExpanded}
				onClick={() => setIsExpanded(!isExpanded)}
				className='hover:bg-accent/50 flex w-full items-center gap-3 px-4 py-3 text-left transition-colors'
			>
				{/* Expand chevron */}
				<div className='flex-shrink-0'>
					{isExpanded ? (
						<ChevronDown className='text-muted-foreground h-4 w-4' />
					) : (
						<ChevronRight className='text-muted-foreground h-4 w-4' />
					)}
				</div>

				{/* Status icon */}
				<div className='flex-shrink-0'>{statusIcon}</div>

				{/* Title and summary */}
				<div className='min-w-0 flex-1'>
					<div className='flex items-center gap-2'>
						{group.stepNumber != null && group.stepNumber > 0 && (
							<span className='flex-shrink-0 rounded bg-blue-500/10 px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-blue-400'>
								Step {group.stepNumber}
							</span>
						)}
						<span className='text-foreground truncate font-medium'>{group.title}</span>
					</div>
					{!isExpanded && <p className='text-muted-foreground mt-0.5 truncate text-xs'>{summary}</p>}
				</div>

				{/* Timestamp */}
				<span className='text-muted-foreground flex-shrink-0 text-xs'>
					{lastTimestamp ? formatTimestamp(lastTimestamp) : ''}
				</span>
			</button>

			{/* Expanded content - activity items */}
			{isExpanded && (
				<div className='border-border/70 bg-card/50 animate-[fadeSlideIn_0.15s_ease-out] border-t'>
					{group.activities.map((activity, idx) => (
						<ActivityItem
							key={`${activity.stepId ?? group.id}-${idx}`}
							activity={activity}
						/>
					))}
				</div>
			)}
		</div>
	);
}

interface ActivityItemProps {
	activity: AgentActivity;
}

/** Resolve the icon for an activity, handling overrides for shared 'classification' category */
function getActivityIcon(activity: AgentActivity): ReactNode {
	if (activity.category === 'classification') {
		if (activity.title === 'User Clarification') {
			return (
				<MessageCircleQuestion
					className={ICON_CLASS}
					strokeWidth={1.5}
				/>
			);
		}
		if (activity.title === 'Reusing Data Pool') return categoryIcons.pool_reuse;
		if (activity.title.includes('Query Plan') || activity.title.includes('Replanned')) {
			return (
				<ListOrdered
					className={ICON_CLASS}
					strokeWidth={1.5}
				/>
			);
		}
	}
	return categoryIcons[activity.category];
}

function QueryResultContent({ content }: { content: string }) {
	const parsed = useMemo(() => {
		try {
			const result = JSON.parse(content);
			return result !== null && typeof result === 'object' ? result : null;
		} catch {
			return null;
		}
	}, [content]);

	if (parsed) {
		return (
			<div className='border-border/50 bg-code-block-bg max-h-64 overflow-auto rounded-md border p-3 font-mono text-xs leading-relaxed'>
				<JsonViewer
					data={parsed}
					defaultExpandDepth={1}
				/>
			</div>
		);
	}

	return (
		<pre className='border-border/50 bg-code-block-bg text-foreground overflow-x-auto rounded-md border p-3 text-xs break-words whitespace-pre-wrap'>
			<code>{content}</code>
		</pre>
	);
}

function ActivityItem({ activity }: ActivityItemProps) {
	const [isExpanded, setIsExpanded] = useState(false);
	const hasContent = activity.content && activity.content.length > 0;

	// Clean up title for sub-items (remove "Step N:" prefix if present)
	const displayTitle = activity.title.replace(/^Step\s+\d+:\s*/i, '');

	if (!hasContent) {
		// Non-expandable item (no content)
		return (
			<div className='border-border/50 flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0'>
				{getActivityIcon(activity)}
				<span className='text-foreground flex-1 truncate text-sm'>{displayTitle}</span>
				<span className='text-muted-foreground text-xs'>{formatTimestamp(activity.timestamp)}</span>
			</div>
		);
	}

	return (
		<div className='border-border/50 border-b last:border-b-0'>
			{/* Expandable row */}
			<button
				type='button'
				aria-expanded={isExpanded}
				onClick={() => setIsExpanded(!isExpanded)}
				className='hover:bg-accent/40 flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors'
			>
				{/* Chevron indicator - shows this is expandable */}
				<div className='flex-shrink-0'>
					{isExpanded ? (
						<ChevronDown className='text-muted-foreground h-3.5 w-3.5' />
					) : (
						<ChevronRight className='text-muted-foreground h-3.5 w-3.5' />
					)}
				</div>

				{/* Category icon */}
				{getActivityIcon(activity)}

				{/* Title */}
				<span className='text-foreground flex-1 truncate text-sm'>{displayTitle}</span>

				{/* Preview of content when collapsed */}
				{!isExpanded && (
					<span className='text-muted-foreground hidden max-w-[200px] truncate text-xs sm:block'>
						{activity.content.length > 60 ? `${activity.content.slice(0, 60)}...` : activity.content}
					</span>
				)}

				{/* Timestamp */}
				<span className='text-muted-foreground flex-shrink-0 text-xs'>{formatTimestamp(activity.timestamp)}</span>
			</button>

			{/* Expanded content */}
			{isExpanded && (
				<div className='ml-[52px] px-4 pt-1 pb-3'>
					{activity.category === 'query_result' ? (
						<QueryResultContent content={activity.content} />
					) : activity.category === 'query_generated' ? (
						<pre className='border-border/50 bg-code-block-bg text-foreground overflow-x-auto rounded-md border p-3 text-xs break-words whitespace-pre-wrap'>
							<code>{activity.content}</code>
						</pre>
					) : activity.category === 'thinking' ? (
						<pre className='text-muted-foreground max-h-48 overflow-y-auto text-sm leading-relaxed break-words whitespace-pre-wrap'>
							{activity.content}
						</pre>
					) : (
						<p className='text-muted-foreground text-sm break-words whitespace-pre-wrap'>{activity.content}</p>
					)}
				</div>
			)}
		</div>
	);
}

/** Skeleton placeholder shown between completed groups and the next step during streaming */
function PendingStepSkeleton({ groups }: { groups: ActivityGroup[] }) {
	const lastGroup = groups[groups.length - 1];
	const { hasResult } = getGroupStatus(lastGroup.activities);
	const lastIsComplete = lastGroup.type === 'header' || hasResult;
	if (!lastIsComplete) return null;

	return (
		<div className='border-border bg-secondary/30 flex animate-pulse items-center gap-3 rounded-lg border px-4 py-3'>
			<Loader2
				className='h-5 w-5 animate-spin text-blue-400'
				strokeWidth={2}
			/>
			<div className='flex-1 space-y-1.5'>
				<div className='bg-secondary/60 h-3.5 w-32 rounded' />
				<div className='bg-secondary/40 h-2.5 w-48 rounded' />
			</div>
		</div>
	);
}

/**
 * Live streaming indicator for thinking text.
 * Only shown during active streaming BEFORE the persisted agent_activity arrives.
 * Once the server emits the thinking activity, this disappears and the
 * persisted version in the GroupCard takes over.
 */
function StreamingThinkingIndicator({ thinkingText }: { thinkingText: string }) {
	const contentRef = useRef<HTMLPreElement>(null);
	useStickToBottom(contentRef, thinkingText, 30);

	return (
		<div className='border-border bg-secondary/50 animate-[fadeSlideIn_0.25s_ease-out] overflow-hidden rounded-lg border'>
			<div className='flex items-center gap-3 px-4 py-3'>
				<Loader2
					className='h-5 w-5 animate-spin text-purple-400'
					strokeWidth={2}
				/>
				<span className='text-foreground font-medium'>Response Generation</span>
				<span className='inline-flex h-2 w-2 animate-pulse rounded-full bg-purple-500' />
			</div>
			<div className='border-border/70 bg-card/50 border-t px-4 py-3'>
				<pre
					ref={contentRef}
					className='text-muted-foreground max-h-48 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap'
				>
					{thinkingText}
					<span className='bg-muted-foreground ml-0.5 inline-block h-3.5 w-1.5 animate-pulse' />
				</pre>
			</div>
		</div>
	);
}

export default function AgentActivityLog({ activities, thinkingText, isStreaming }: AgentActivityLogProps) {
	const groups = useMemo(() => groupActivities(activities), [activities]);
	const containerRef = useRef<HTMLDivElement>(null);

	// Check if the format_response thinking activity has arrived (persisted after streaming completes).
	// Only check for "Model Reasoning" — other thinking activities (classification, planning, etc.) arrive earlier.
	const hasPersistedThinking = activities.some((a) => a.category === 'thinking' && a.title === 'Model Reasoning');
	// Show streaming indicator only while actively streaming AND before activity arrives
	const showStreamingThinking = isStreaming && !!thinkingText && !hasPersistedThinking;

	// Sticky scroll: only auto-scroll when user is near the bottom
	useStickToBottom(containerRef, `${activities.length}-${thinkingText}`);

	if (activities.length === 0 && !showStreamingThinking) {
		return null;
	}

	// If no groups yet or only header activities, show simpler view
	if (groups.length === 0 && !showStreamingThinking) {
		return (
			<div className='text-muted-foreground text-sm'>
				<p>Processing query...</p>
			</div>
		);
	}

	return (
		<div
			ref={containerRef}
			className='max-h-[400px] space-y-2 overflow-y-auto'
		>
			{groups.map((group, idx) => (
				<GroupCard
					key={group.id}
					group={group}
					isStreaming={!!isStreaming}
					defaultExpanded={idx === groups.length - 1}
				/>
			))}
			{isStreaming && groups.length > 0 && !groups.some((g) => g.type === 'response') && !showStreamingThinking && (
				<PendingStepSkeleton groups={groups} />
			)}
			{showStreamingThinking && <StreamingThinkingIndicator thinkingText={thinkingText} />}
		</div>
	);
}
